import React, { useState, useMemo } from 'react';
import { Modal, View, Text, TouchableOpacity, ScrollView, TouchableWithoutFeedback, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { venueStyles } from '../styles/venueStyles';
import { RoomEqPhoto } from './RoomEqPhoto';

const getRoomEqMediaType = (url) => {
  if (!url || typeof url !== 'string') return 'image';
  const ext = url.split('.').pop()?.toLowerCase()?.split('?')[0] || '';
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(ext)) return 'video';
  return 'image';
};

/**
 * Reusable Room EQ viewer modal showing EQ / RF / custom tabs for a list of submissions.
 *
 * submissions: [{ id, type: 'roomEqSubmission', eq, rf, addTab, uploaderDisplayName, ... }]
 */
export function RoomEqViewerModal({
  visible,
  submissions,
  initialIndex = 0,
  onClose,
  onDeleteSubmission,
  onEditSubmission,
  /** Close modal then navigate to venue (e.g. profile / media manager parent). */
  onGoToVenue,
}) {
  const [index, setIndex] = useState(initialIndex);
  const [activeTab, setActiveTab] = useState('EQ');

  const hasSubmissions = Array.isArray(submissions) && submissions.length > 0;
  const current = hasSubmissions ? submissions[index] || submissions[0] : null;

  const initialTab = useMemo(() => {
    if (!current) return 'EQ';
    if (current.eq) return 'EQ';
    if (current.rf) return 'RF';
    if (current.addTab) return 'ADD';
    return 'EQ';
  }, [current]);

  // Keep activeTab in sync when changing submissions
  React.useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  React.useEffect(() => {
    if (visible) {
      setIndex(initialIndex);
    }
  }, [visible, initialIndex]);

  const handlePrev = () => {
    if (!hasSubmissions) return;
    const n = submissions.length;
    setIndex((prev) => (prev > 0 ? prev - 1 : n - 1));
  };

  const handleNext = () => {
    if (!hasSubmissions) return;
    const n = submissions.length;
    setIndex((prev) => (prev < n - 1 ? prev + 1 : 0));
  };

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={venueStyles.roomEqMacOverlay}>
          <TouchableWithoutFeedback onPress={() => {}}>
            <View style={venueStyles.roomEqMacWindowWrap}>
              <View style={venueStyles.roomEqMacViewerRow}>
                {hasSubmissions && (
                  <TouchableOpacity style={venueStyles.roomEqMacViewerArrowOuter} onPress={handlePrev}>
                    <Ionicons name="chevron-back" size={32} color="#FFFFFF" />
                  </TouchableOpacity>
                )}
                <View style={venueStyles.roomEqMacWindow}>
                  {current ? (
                    <>
                      {/* Tabs */}
                      <View style={{ flexDirection: 'row', paddingHorizontal: 12, justifyContent: 'center' }}>
                        {current.eq ? (
                          <TouchableOpacity
                            onPress={() => setActiveTab('EQ')}
                            style={{
                              paddingVertical: 12,
                              paddingHorizontal: 16,
                              borderBottomWidth: 2,
                              borderBottomColor: activeTab === 'EQ' ? '#000' : 'transparent',
                              marginBottom: -1,
                            }}
                          >
                            <Text
                              style={{
                                fontSize: 32,
                                fontWeight: activeTab === 'EQ' ? '600' : '400',
                                color: '#000',
                                fontFamily: 'Computer Says No',
                              }}
                            >
                              EQ
                            </Text>
                          </TouchableOpacity>
                        ) : null}
                        {current.rf ? (
                          <TouchableOpacity
                            onPress={() => setActiveTab('RF')}
                            style={{
                              paddingVertical: 12,
                              paddingHorizontal: 16,
                              borderBottomWidth: 2,
                              borderBottomColor: activeTab === 'RF' ? '#000' : 'transparent',
                              marginBottom: -1,
                            }}
                          >
                            <Text
                              style={{
                                fontSize: 32,
                                fontWeight: activeTab === 'RF' ? '600' : '400',
                                color: '#000',
                                fontFamily: 'Computer Says No',
                              }}
                            >
                              RF
                            </Text>
                          </TouchableOpacity>
                        ) : null}
                        {current.addTab ? (
                          <TouchableOpacity
                            onPress={() => setActiveTab('ADD')}
                            style={{
                              paddingVertical: 12,
                              paddingHorizontal: 16,
                              borderBottomWidth: 2,
                              borderBottomColor: activeTab === 'ADD' ? '#000' : 'transparent',
                              marginBottom: -1,
                            }}
                          >
                            <Text
                              style={{
                                fontSize: 32,
                                fontWeight: activeTab === 'ADD' ? '600' : '400',
                                color: '#000',
                                fontFamily: 'Computer Says No',
                              }}
                            >
                              {current.addTab.title || 'Add Tab'}
                            </Text>
                          </TouchableOpacity>
                        ) : null}
                      </View>

                      {/* Content */}
                      <View style={venueStyles.roomEqMacContent}>
                        {activeTab === 'EQ' && current.eq ? (
                          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16 }}>
                            {(current.eq.photos || []).map((p, i) => (
                              <View key={i} style={{ marginBottom: 16 }}>
                                <RoomEqPhoto
                                  uri={p.url}
                                  description={p.description}
                                  style={{ width: '100%', height: 200, borderRadius: 8 }}
                                />
                                {p.description ? (
                                  <Text
                                    style={{
                                      fontSize: 24,
                                      color: '#000',
                                      marginTop: 4,
                                      fontFamily: 'Computer Says No',
                                    }}
                                  >
                                    {p.description}
                                  </Text>
                                ) : null}
                              </View>
                            ))}
                            {(current.eq.notes || []).length > 0 ? (
                              <View style={{ marginTop: 16 }}>
                                <Text
                                  style={{
                                    fontSize: 24,
                                    fontWeight: '600',
                                    color: '#000',
                                    marginBottom: 8,
                                    fontFamily: 'Computer Says No',
                                  }}
                                >
                                  Notes
                                </Text>
                                {(current.eq.notes || []).map((n, i) => (
                                  <Text
                                    key={i}
                                    style={{
                                      fontSize: 22,
                                      color: '#000',
                                      marginBottom: 4,
                                      fontFamily: 'Computer Says No',
                                    }}
                                  >
                                    {n}
                                  </Text>
                                ))}
                              </View>
                            ) : null}
                            {(current.eq.setlist || []).length > 0 ? (
                              <View
                                style={{
                                  marginTop: 16,
                                  paddingTop: 16,
                                  borderTopWidth: 1,
                                  borderTopColor: '#000',
                                }}
                              >
                                <Text
                                  style={{
                                    fontSize: 24,
                                    fontWeight: '600',
                                    color: '#000',
                                    marginBottom: 8,
                                    fontFamily: 'Computer Says No',
                                  }}
                                >
                                  PA Playlist
                                </Text>
                                {(current.eq.setlist || []).map((s, i) => (
                                  <Text
                                    key={i}
                                    style={{
                                      fontSize: 22,
                                      color: '#000',
                                      marginBottom: 4,
                                      fontFamily: 'Computer Says No',
                                    }}
                                  >
                                    {i + 1}. {s}
                                  </Text>
                                ))}
                              </View>
                            ) : null}
                          </ScrollView>
                        ) : activeTab === 'RF' && current.rf ? (
                          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16 }}>
                            {(current.rf.textItems || []).length > 0 ? (
                              <View
                                style={{
                                  flexDirection: 'row',
                                  flexWrap: 'wrap',
                                  marginHorizontal: -6,
                                  marginBottom: 12,
                                }}
                              >
                                {current.rf.textItems.map((t, i) => (
                                  <View key={i} style={{ width: '50%', padding: 6 }}>
                                    <Text
                                      style={{
                                        borderWidth: 1,
                                        borderColor: '#000',
                                        borderRadius: 8,
                                        padding: 12,
                                        fontSize: 20,
                                        color: '#000',
                                        fontFamily: 'Computer Says No',
                                        minHeight: 60,
                                      }}
                                    >
                                      {t}
                                    </Text>
                                  </View>
                                ))}
                              </View>
                            ) : null}
                            {(current.rf.photos || []).map((p, i) => (
                              <View key={i} style={{ marginBottom: 12 }}>
                                <RoomEqPhoto
                                  uri={p.url}
                                  description={p.description}
                                  style={{ width: '100%', height: 200, borderRadius: 8 }}
                                />
                                {p.description ? (
                                  <Text
                                    style={{
                                      fontSize: 24,
                                      color: '#000',
                                      marginTop: 4,
                                      fontFamily: 'Computer Says No',
                                    }}
                                  >
                                    {p.description}
                                  </Text>
                                ) : null}
                              </View>
                            ))}
                          </ScrollView>
                        ) : activeTab === 'ADD' && current.addTab ? (
                          current.addTab.formType === 'EQ' ? (
                            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16 }}>
                              {(current.addTab.data?.photos || []).map((p, i) => (
                                <View key={i} style={{ marginBottom: 16 }}>
                                  <RoomEqPhoto
                                    uri={p.url}
                                    description={p.description}
                                    style={{ width: '100%', height: 200, borderRadius: 8 }}
                                  />
                                  {p.description ? (
                                    <Text
                                      style={{
                                        fontSize: 24,
                                        color: '#000',
                                        marginTop: 4,
                                        fontFamily: 'Computer Says No',
                                      }}
                                    >
                                      {p.description}
                                    </Text>
                                  ) : null}
                                </View>
                              ))}
                              {(current.addTab.data?.notes || []).length > 0 ? (
                                <View style={{ marginTop: 16 }}>
                                  <Text
                                    style={{
                                      fontSize: 20,
                                      fontWeight: '600',
                                      color: '#000',
                                      marginBottom: 8,
                                      fontFamily: 'Computer Says No',
                                    }}
                                  >
                                    Notes
                                  </Text>
                                  {(current.addTab.data.notes || []).map((n, i) => (
                                    <Text
                                      key={i}
                                      style={{
                                        fontSize: 20,
                                        color: '#000',
                                        marginBottom: 4,
                                        fontFamily: 'Computer Says No',
                                      }}
                                    >
                                      {n}
                                    </Text>
                                  ))}
                                </View>
                              ) : null}
                              {(current.addTab.data?.setlist || []).length > 0 ? (
                                <View
                                  style={{
                                    marginTop: 16,
                                    paddingTop: 16,
                                    borderTopWidth: 1,
                                    borderTopColor: '#000',
                                  }}
                                >
                                  <Text
                                    style={{
                                      fontSize: 20,
                                      fontWeight: '600',
                                      color: '#000',
                                      marginBottom: 8,
                                      fontFamily: 'Computer Says No',
                                    }}
                                  >
                                    Setlist
                                  </Text>
                                  {(current.addTab.data.setlist || []).map((s, i) => (
                                    <Text
                                      key={i}
                                      style={{
                                        fontSize: 20,
                                        color: '#000',
                                        marginBottom: 4,
                                        fontFamily: 'Computer Says No',
                                      }}
                                    >
                                      {i + 1}. {s}
                                    </Text>
                                  ))}
                                </View>
                              ) : null}
                            </ScrollView>
                          ) : (
                            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16 }}>
                              {(current.addTab.data?.textItems || []).length > 0 ? (
                                <View
                                  style={{
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    marginHorizontal: -6,
                                    marginBottom: 12,
                                  }}
                                >
                                  {current.addTab.data.textItems.map((t, i) => (
                                    <View key={i} style={{ width: '50%', padding: 6 }}>
                                      <Text
                                        style={{
                                          borderWidth: 1,
                                          borderColor: '#000',
                                          borderRadius: 8,
                                          padding: 12,
                                          fontSize: 24,
                                          color: '#000',
                                          fontFamily: 'Computer Says No',
                                          minHeight: 60,
                                        }}
                                      >
                                        {t}
                                      </Text>
                                    </View>
                                  ))}
                                </View>
                              ) : null}
                              {(current.addTab.data?.photos || []).map((p, i) => (
                                <View key={i} style={{ marginBottom: 12 }}>
                                  <RoomEqPhoto
                                    uri={p.url}
                                    description={p.description}
                                    style={{ width: '100%', height: 200, borderRadius: 8 }}
                                  />
                                  {p.description ? (
                                    <Text
                                      style={{
                                        fontSize: 24,
                                        color: '#000',
                                        marginTop: 4,
                                        fontFamily: 'Computer Says No',
                                      }}
                                    >
                                      {p.description}
                                    </Text>
                                  ) : null}
                                </View>
                              ))}
                            </ScrollView>
                          )
                        ) : null}
                      </View>

                      {/* Footer with uploader + actions */}
                      <View style={venueStyles.roomEqMacViewerFooter}>
                        <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
                          {onGoToVenue && current?.venueId ? (
                            <TouchableOpacity
                              onPress={() => onGoToVenue(current)}
                              style={{ paddingVertical: 6, paddingHorizontal: 12, backgroundColor: '#e8f4fc', borderRadius: 6 }}
                            >
                              <Text style={{ fontSize: 16, color: '#000', fontFamily: 'Computer Says No' }}>View venue</Text>
                            </TouchableOpacity>
                          ) : null}
                          {onEditSubmission && (
                            <TouchableOpacity
                              onPress={() => onEditSubmission(current)}
                              style={{ paddingVertical: 6, paddingHorizontal: 12, backgroundColor: '#fff', borderRadius: 6 }}
                            >
                              <Text style={{ fontSize: 16, color: '#000', fontFamily: 'Computer Says No' }}>Edit</Text>
                            </TouchableOpacity>
                          )}
                          {onDeleteSubmission && (
                            <TouchableOpacity
                              onPress={() => onDeleteSubmission(current)}
                              style={{ paddingVertical: 6, paddingHorizontal: 12, backgroundColor: '#ff4d4f', borderRadius: 6 }}
                            >
                              <Text style={{ fontSize: 16, color: '#fff', fontFamily: 'Computer Says No' }}>Delete</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                        <Text style={venueStyles.roomEqMacViewerUploader} numberOfLines={1}>
                          {current.uploaderDisplayName || 'Anonymous'}
                        </Text>
                      </View>
                    </>
                  ) : null}
                </View>
                {hasSubmissions && (
                  <TouchableOpacity style={venueStyles.roomEqMacViewerArrowOuter} onPress={handleNext}>
                    <Ionicons name="chevron-forward" size={32} color="#FFFFFF" />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
}

